import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('zh') ? 'zh' : 'en';

  return (
    <main className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="legal-brand">{t('home.brand')}</Link>
        <Link to="/" className="legal-back">← {t('common.home')}</Link>
      </nav>

      <div className="legal-container">
        {lang === 'en' ? (
          <>
            <h1>Privacy Policy</h1>
            <p className="legal-date">Last updated: June 2025</p>

            <section>
              <h2>1. Introduction</h2>
              <p>Incord (UK) is a trading name of Lucky Wheel 11 Ltd. (Company No: 12106600). We are committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website incord.uk or use our procurement services.</p>
            </section>

            <section>
              <h2>2. Data We Collect</h2>
              <p>We may collect the following categories of personal data:</p>
              <ul>
                <li><strong>Identity Data:</strong> full name, company name, job title.</li>
                <li><strong>Contact Data:</strong> email address, phone number, shipping/billing address, WhatsApp ID.</li>
                <li><strong>Financial Data:</strong> payment card details (processed via Stripe — we do not store card numbers), transaction history.</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies and usage data.</li>
                <li><strong>Profile Data:</strong> your preferences, order history, membership status, reward balance.</li>
              </ul>
            </section>

            <section>
              <h2>3. How We Use Your Data</h2>
              <p>Your data is used solely for the following purposes:</p>
              <ul>
                <li>To process procurement orders, customs clearance, and export documentation.</li>
                <li>To manage your membership account and reward programme.</li>
                <li>To process payments and prevent fraudulent transactions.</li>
                <li>To communicate order updates, shipping tracking, and customer support.</li>
                <li>To comply with UK legal and regulatory obligations (HMRC, anti-money laundering).</li>
                <li>To improve our website and services through aggregated analytics.</li>
              </ul>
            </section>

            <section>
              <h2>4. Lawful Basis</h2>
              <p>Under UK GDPR, we process your data on the following lawful bases:</p>
              <ul>
                <li><strong>Contractual Necessity:</strong> to fulfil our procurement and logistics services.</li>
                <li><strong>Legal Obligation:</strong> to comply with UK export, customs, and tax regulations.</li>
                <li><strong>Legitimate Interests:</strong> to improve our services, prevent fraud, and ensure network security.</li>
                <li><strong>Consent:</strong> where you have opted in to marketing communications (you may withdraw at any time).</li>
              </ul>
            </section>

            <section>
              <h2>5. Data Sharing</h2>
              <p>We share your data only with trusted third parties essential to our service:</p>
              <ul>
                <li><strong>Stripe</strong> — payment processing (PCI DSS compliant).</li>
                <li><strong>DHL, UPS, FedEx</strong> — international shipping and logistics.</li>
                <li><strong>HMRC & UK Customs</strong> — export declaration and VAT compliance.</li>
                <li><strong>Cloud service providers</strong> — secure data hosting (EU/UK based).</li>
              </ul>
              <p>We never sell your personal data to third parties.</p>
            </section>

            <section>
              <h2>6. Data Retention</h2>
              <p>We retain your personal data for as long as your account is active, plus up to 6 years following account closure to comply with UK tax and anti-money laundering regulations.</p>
            </section>

            <section>
              <h2>7. Your Rights</h2>
              <p>Under UK GDPR, you have the right to:</p>
              <ul>
                <li>Access your personal data held by us.</li>
                <li>Rectify inaccurate or incomplete data.</li>
                <li>Request erasure of your data ("right to be forgotten").</li>
                <li>Restrict or object to processing.</li>
                <li>Data portability.</li>
                <li>Withdraw consent at any time (without affecting the lawfulness of processing before withdrawal).</li>
              </ul>
              <p>To exercise any of these rights, contact us at <a href="mailto:hello@incord.uk">hello@incord.uk</a>.</p>
            </section>

            <section>
              <h2>8. Security</h2>
              <p>We implement appropriate technical and organisational measures to protect your data, including SSL/TLS encryption, secure payment gateways (Stripe), access controls, and regular security audits.</p>
            </section>

            <section>
              <h2>9. Contact</h2>
              <p>If you have any questions about this Privacy Policy or wish to make a complaint, please contact us:</p>
              <p>
                <strong>Lucky Wheel 11 Ltd.</strong><br />
                Registered Office: 169 Great Portland St, London, United Kingdom.<br />
                Email: <a href="mailto:hello@incord.uk">hello@incord.uk</a><br />
                Company No: 12106600
              </p>
            </section>
          </>
        ) : (
          <>
            <h1>隱私權政策</h1>
            <p className="legal-date">更新日期：2025年6月</p>

            <section>
              <h2>1. 簡介</h2>
              <p>Incord (UK) 為 Lucky Wheel 11 Ltd. 之交易名稱（公司註冊號：12106600）。我們致力於保護您的個人資料並尊重您的隱私。本隱私權政策說明我們在您造訪 incord.uk 網站或使用採購服務時，如何收集、使用、揭露及保護您的資訊。</p>
            </section>

            <section>
              <h2>2. 我們收集的資料</h2>
              <p>我們可能收集以下類別的個人資料：</p>
              <ul>
                <li><strong>身分資料：</strong>姓名、公司名稱、職稱。</li>
                <li><strong>聯絡資料：</strong>電子郵件、電話號碼、運送/帳單地址、WhatsApp ID。</li>
                <li><strong>財務資料：</strong>支付卡資訊（透過 Stripe 處理，我們不儲存卡號）、交易記錄。</li>
                <li><strong>技術資料：</strong>IP 位址、瀏覽器類型、裝置資訊、Cookie 及使用數據。</li>
                <li><strong>個人檔案資料：</strong>您的偏好、訂單記錄、會員狀態、紅利餘額。</li>
              </ul>
            </section>

            <section>
              <h2>3. 資料使用方式</h2>
              <p>您的資料僅用於以下目的：</p>
              <ul>
                <li>處理採購訂單、報關及出口文件。</li>
                <li>管理您的會員帳戶及紅利計劃。</li>
                <li>處理付款及防止詐騙交易。</li>
                <li>通知訂單更新、運送追蹤及客戶支援。</li>
                <li>遵守英國法律及監管義務（HMRC、反洗錢）。</li>
                <li>透過匯總分析改善網站及服務。</li>
              </ul>
            </section>

            <section>
              <h2>4. 法律依據</h2>
              <p>根據英國 GDPR，我們基於以下法律依據處理您的資料：</p>
              <ul>
                <li><strong>合約必要性：</strong>履行採購及物流服務。</li>
                <li><strong>法律義務：</strong>遵守英國出口、關務及稅務法規。</li>
                <li><strong>合法利益：</strong>改善服務、防止詐騙、確保網路安全。</li>
                <li><strong>同意：</strong>您同意接收行銷通訊（可隨時撤回）。</li>
              </ul>
            </section>

            <section>
              <h2>5. 資料分享</h2>
              <p>我們僅與服務所需之可信第三方分享您的資料：</p>
              <ul>
                <li><strong>Stripe</strong> — 付款處理（PCI DSS 合規）。</li>
                <li><strong>DHL、UPS、FedEx</strong> — 國際運送及物流。</li>
                <li><strong>HMRC 及英國海關</strong> — 出口申報及 VAT 合規。</li>
                <li><strong>雲端服務供應商</strong> — 安全資料託管（位於歐盟/英國）。</li>
              </ul>
              <p>我們絕不將您的個人資料出售給第三方。</p>
            </section>

            <section>
              <h2>6. 資料保留</h2>
              <p>我們在您的帳戶有效期間保留您的個人資料，並在帳戶關閉後保留最多 6 年，以遵守英國稅務及反洗錢法規。</p>
            </section>

            <section>
              <h2>7. 您的權利</h2>
              <p>根據英國 GDPR，您有權：</p>
              <ul>
                <li>查閱我們持有的您的個人資料。</li>
                <li>更正不準確或不完整的資料。</li>
                <li>要求刪除您的資料（「被遺忘權」）。</li>
                <li>限制或反對資料處理。</li>
                <li>資料可攜權。</li>
                <li>隨時撤回同意（不影響撤回前處理的合法性）。</li>
              </ul>
              <p>如欲行使任何權利，請寄送電子郵件至 <a href="mailto:hello@incord.uk">hello@incord.uk</a>。</p>
            </section>

            <section>
              <h2>8. 安全措施</h2>
              <p>我們實施適當的技術及組織措施保護您的資料，包括 SSL/TLS 加密、安全支付閘道（Stripe）、存取控制及定期安全稽核。</p>
            </section>

            <section>
              <h2>9. 聯絡資訊</h2>
              <p>如果您對本隱私權政策有任何疑問或欲提出申訴，請聯絡我們：</p>
              <p>
                <strong>Lucky Wheel 11 Ltd.</strong><br />
                註冊地址：169 Great Portland St, London, United Kingdom.<br />
                電子郵件：<a href="mailto:hello@incord.uk">hello@incord.uk</a><br />
                公司註冊號：12106600
              </p>
            </section>
          </>
        )}
      </div>

      <footer className="legal-footer">
        <div className="container">
          <Link to="/" style={{ color: 'var(--accent-gold)', textDecoration: 'none' }}>← {t('common.home')}</Link>
        </div>
      </footer>
    </main>
  );
};

export default PrivacyPolicy;
